/**
 * Event landing-page template — 01-webinar ("Executive Briefing").
 *
 * Distinct CraftJS home-page document for the `webinar` theme. Unlike the
 * legacy shared `welcome` template (one doc filtered per theme), each event
 * template owns its full content/layout. Authored from the design spec in
 *   TEMPLATES - LANDINPAGES/01-webinar/sections/*.html (CRAFT_PROPS_HINT blocks).
 *
 * Convention (mirrors temp.ts/welcome):
 *  - Section-level marketing copy (headlines, paragraphs, button labels) goes
 *    through td() keys (websiteBuilderDefaults, `wbn*` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    resolves through td() keys (Wave 3b); only fictitious proper names of
 *    people/companies stay inline.
 *  - Global colors/fonts come from WEBINAR_PRESET (applied via the theme);
 *    only sections with their own surface (Hero/Testimonials over dark) set
 *    explicit per-node colors here.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  PAD_CARD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import { direction } from "@/lib/designDirections/directions";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Webinar template photos (canonical location: <CDN>/shared/templates/01-webinar/…)
const W = `${A}/shared/templates/01-webinar`;

// WS4-B2: webinar palette is registry-synced — the values are byte-equal to the
// shared design-directions canon, so the consts read straight from it.
const ACCENT = direction("webinar").primary; // corporate blue (#1E40AF)
const INK = direction("webinar").text; // near-black headings (#0F172A)
const MUTE = direction("webinar").accent; // muted body (#475569)
const CREAM = direction("webinar").background; // warm surface (#F7F2ED)
const DARK = direction("webinar").text; // dark section surface (#0F172A)
const ON_DARK = "#e2e8f0"; // body text on dark
const ON_DARK_ACCENT = "#93c5fd"; // eyebrow on dark
const SERIF = "Georgia, 'Times New Roman', serif";

// Image-dependent sections reference the template photo library under
// shared/templates/01-webinar/* and default to hidden (like the shared
// `welcome` template) so the page renders clean; users enable them once they
// add their own media.
const LOGO = `${A}/shared/logo.svg`;

// ── Inline child data (localized via td(); matches 01-webinar copy) ───
// Fictitious proper names (people/companies) stay inline; every other
// user-visible string resolves through websiteBuilderDefaults keys.

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>500+</b>", desc: td("wbnNum1Desc") },
  { num: "<b>4,8 / 5</b>", desc: td("wbnNum2Desc") },
  { num: "<b>3</b>", desc: td("wbnNum3Desc") },
  { num: "<b>90</b>", desc: td("wbnNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { title: td("wbnAgendaRow1Title"), speaker: td("wbnAgendaSpeakerHost"), desc: td("wbnAgendaRow1Desc") },
  { title: td("wbnAgendaRow2Title"), speaker: "Dr. Lena Brandt", desc: td("wbnAgendaRow2Desc") },
  { title: td("wbnAgendaRow3Title"), speaker: "Marco Feldmann", desc: td("wbnAgendaRow3Desc") },
  { title: td("wbnAgendaRow4Title"), speaker: td("wbnAgendaSpeakerAll"), desc: td("wbnAgendaRow4Desc") },
  { title: td("wbnAgendaRow5Title"), speaker: td("wbnAgendaSpeakerHost"), desc: td("wbnAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Dr. Lena Brandt", role: td("wbnSpeaker1Role"), img: "03" },
  { name: "Marco Feldmann", role: td("wbnSpeaker2Role"), img: "06" },
  { name: "Sophie Rauch", role: td("wbnSpeaker3Role"), img: "05" },
  { name: "Jonas Weiß", role: td("wbnSpeaker4Role"), img: "12" },
  { name: "Aylin Kaya", role: td("wbnSpeaker5Role"), img: "01" },
  { name: "Tobias Frei", role: td("wbnSpeaker6Role"), img: "04" },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("wbnTesti1Quote"), name: "Christine Vogt", role: td("wbnTesti1Role"), img: "05" },
  { quote: td("wbnTesti2Quote"), name: "Rafael Moreno", role: td("wbnTesti2Role"), img: "06" },
  { quote: td("wbnTesti3Quote"), name: "Nadia Hofer", role: td("wbnTesti3Role"), img: "03" },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("wbnBlog1Topic")}</b>`, content: td("wbnBlog1Content") },
  { topic: `<b>${td("wbnBlog2Topic")}</b>`, content: td("wbnBlog2Content") },
  { topic: `<b>${td("wbnBlog3Topic")}</b>`, content: td("wbnBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("wbnFaq1Question")}</b>`, a: td("wbnFaq1Answer") },
  { q: `<b>${td("wbnFaq2Question")}</b>`, a: td("wbnFaq2Answer") },
  { q: `<b>${td("wbnFaq3Question")}</b>`, a: td("wbnFaq3Answer") },
  { q: `<b>${td("wbnFaq4Question")}</b>`, a: td("wbnFaq4Answer") },
  { q: `<b>${td("wbnFaq5Question")}</b>`, a: td("wbnFaq5Answer") },
  { q: `<b>${td("wbnFaq6Question")}</b>`, a: td("wbnFaq6Answer") },
];

// ── Webinar-specific child factories ──────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: `<b>${td("wbnAgendaTimePlaceholder")}</b>`, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-headphones.jpg`, `${W}/hero-dokumente.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/detail-laptop.jpg`, `${W}/hero-absage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const webinar = (td: TranslationFn): string => {
  const B = "wbn_body";

  // Top-level section ids (body order)
  const HERO = "wbn_hero";
  const NUMBERS = "wbn_numbers";
  const AGENDA = "wbn_agenda";
  const SPEAKER = "wbn_speaker";
  const TEXT = "wbn_text";
  const IMAGE = "wbn_image";
  const COUNTDOWN = "wbn_countdown";
  const GALLERY = "wbn_gallery";
  const VIDEO = "wbn_video";
  const TESTI = "wbn_testimonials";
  const BLOG = "wbn_blog";
  const FAQ = "wbn_faq";
  const MAP = "wbn_map";
  const DI = "wbn_datainput";
  const CONTACT = "wbn_contact";

  return enc({
    ...scaffold(
      { ab: "wbn_ab", abs: "wbn_abs", body: B, ft: "wbn_ft", fts: "wbn_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-none" },
    ),

    // ── Hero (image right, dark text on white) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        // imageLayout:"right" → image on the right, text left (desktop, ≥wb-md).
        // inversed:true → on mobile the column stacks text-first, image below.
        sectionHeight: 85, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("wbnHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("wbnHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("wbnHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("wbnHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("wbnHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: "#ffffff",
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_hero_eb", header: "wbn_hero_hd", text: "wbn_hero_tx", buttonOne: "wbn_hero_b1", buttonTwo: "wbn_hero_b2" },
    },
    wbn_hero_eb: tc(HERO, { isVisible: true, text: td("wbnHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wbn_hero_hd: tc(HERO, { isVisible: true, text: td("wbnHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    wbn_hero_tx: tc(HERO, { isVisible: true, text: td("wbnHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    wbn_hero_b1: btn(HERO, { type: "secondary", text: td("wbnHeroSecondaryButton"), displayName: "buttonOne" }),
    wbn_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("wbnHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["wbn_num1", "wbn_num2", "wbn_num3", "wbn_num4"],
      linkedNodes: { headline: "wbn_num_eb", header: "wbn_num_hd", text: "wbn_num_tx" },
    },
    wbn_num_eb: tc(NUMBERS, { isVisible: false, text: td("wbnNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_num_hd: tc(NUMBERS, { isVisible: true, text: td("wbnNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_num_tx: tc(NUMBERS, { isVisible: true, text: td("wbnNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_num1: numbersChild(NUMBERS, 0, td),
    wbn_num2: numbersChild(NUMBERS, 1, td),
    wbn_num3: numbersChild(NUMBERS, 2, td),
    wbn_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["wbn_ag1", "wbn_ag2", "wbn_ag3", "wbn_ag4", "wbn_ag5"],
      linkedNodes: { headline: "wbn_ag_eb", header: "wbn_ag_hd", text: "wbn_ag_tx" },
    },
    wbn_ag_eb: tc(AGENDA, { isVisible: false, text: td("wbnAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_ag_hd: tc(AGENDA, { isVisible: true, text: td("wbnAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_ag_tx: tc(AGENDA, { isVisible: true, text: td("wbnAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_ag1: agendaChild(AGENDA, 0, td),
    wbn_ag2: agendaChild(AGENDA, 1, td),
    wbn_ag3: agendaChild(AGENDA, 2, td),
    wbn_ag4: agendaChild(AGENDA, 3, td),
    wbn_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["wbn_sp1", "wbn_sp2", "wbn_sp3", "wbn_sp4", "wbn_sp5", "wbn_sp6"],
      linkedNodes: { headline: "wbn_sp_eb", header: "wbn_sp_hd", text: "wbn_sp_tx" },
    },
    wbn_sp_eb: tc(SPEAKER, { isVisible: false, text: td("wbnSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_sp_hd: tc(SPEAKER, { isVisible: true, text: td("wbnSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_sp_tx: tc(SPEAKER, { isVisible: true, text: td("wbnSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_sp1: speakerChild(SPEAKER, 0, td),
    wbn_sp2: speakerChild(SPEAKER, 1, td),
    wbn_sp3: speakerChild(SPEAKER, 2, td),
    wbn_sp4: speakerChild(SPEAKER, 3, td),
    wbn_sp5: speakerChild(SPEAKER, 4, td),
    wbn_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial note from host) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("wbnTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("wbnTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_tx_eb", header: "wbn_tx_hd", text: "wbn_tx_tx", buttonOne: "wbn_tx_b1", buttonTwo: "wbn_tx_b2" },
    },
    wbn_tx_eb: tc(TEXT, { isVisible: false, text: td("wbnTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_tx_hd: tc(TEXT, { isVisible: true, text: td("wbnTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    wbn_tx_tx: tc(TEXT, { isVisible: true, text: td("wbnTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_tx_b1: btn(TEXT, { type: "secondary", text: td("wbnTextSecondaryButton"), displayName: "buttonOne" }),
    wbn_tx_b2: btn(TEXT, { type: "primary", text: td("wbnTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (detail, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("wbnImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wbnImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wbnImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wbnImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-none", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-headphones.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_im_eb", header: "wbn_im_hd", text: "wbn_im_tx", button: "wbn_im_btn", image: "wbn_im_img" },
    },
    wbn_im_eb: tc(IMAGE, { isVisible: false, text: td("wbnImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    wbn_im_hd: tc(IMAGE, { isVisible: true, text: td("wbnImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wbn_im_tx: tc(IMAGE, { isVisible: true, text: td("wbnImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wbn_im_btn: btn(IMAGE, { text: td("wbnImageButton"), displayName: "button" }),
    wbn_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-headphones.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("wbnCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wbnCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wbnCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wbnCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#64748b", countdownBorderColor: "#e2d8c8",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_cd_eb", header: "wbn_cd_hd", text: "wbn_cd_tx", button: "wbn_cd_btn" },
    },
    wbn_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("wbnCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wbn_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("wbnCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    wbn_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("wbnCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    wbn_cd_btn: btn(COUNTDOWN, { text: td("wbnCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (backstage) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",        isVisibleHeadline: false, textHeadline: td("wbnGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("wbnGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("wbnGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("wbnGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["wbn_g1", "wbn_g2", "wbn_g3", "wbn_g4", "wbn_g5", "wbn_g6"],
      linkedNodes: { headline: "wbn_g_eb", header: "wbn_g_hd", text: "wbn_g_tx", secondary: "wbn_g_b2", primary: "wbn_g_b1" },
    },
    wbn_g_eb: tc(GALLERY, { isVisible: false, text: td("wbnGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_g_hd: tc(GALLERY, { isVisible: true, text: td("wbnGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_g_tx: tc(GALLERY, { isVisible: false, text: td("wbnGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_g_b2: btn(GALLERY, { type: "secondary", text: td("wbnGallerySecondaryButton"), displayName: "ButtonChild" }),
    wbn_g_b1: btn(GALLERY, { text: td("wbnGalleryPrimaryButton"), displayName: "ButtonChild" }),
    wbn_g1: galleryImage(GALLERY, 0),
    wbn_g2: galleryImage(GALLERY, 1),
    wbn_g3: galleryImage(GALLERY, 2),
    wbn_g4: galleryImage(GALLERY, 3),
    wbn_g5: galleryImage(GALLERY, 4),
    wbn_g6: galleryImage(GALLERY, 5),

    // ── Video (trailer) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("wbnVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("wbnVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_vd_eb", header: "wbn_vd_hd", text: "wbn_vd_tx", button: "wbn_vd_btn" },
    },
    wbn_vd_eb: tc(VIDEO, { isVisible: false, text: td("wbnVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_vd_hd: tc(VIDEO, { isVisible: true, text: td("wbnVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_vd_tx: tc(VIDEO, { isVisible: true, text: td("wbnVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_vd_btn: btn(VIDEO, { text: td("wbnVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("wbnTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("wbnTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["wbn_ts1", "wbn_ts2", "wbn_ts3"],
      linkedNodes: { headline: "wbn_ts_eb", header: "wbn_ts_hd", text: "wbn_ts_tx" },
    },
    wbn_ts_eb: tc(TESTI, { isVisible: true, text: td("wbnTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    wbn_ts_hd: tc(TESTI, { isVisible: true, text: td("wbnTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    wbn_ts_tx: tc(TESTI, { isVisible: false, text: td("wbnTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    wbn_ts1: testimonialChild(TESTI, 0, td),
    wbn_ts2: testimonialChild(TESTI, 1, td),
    wbn_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (pre-reading) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["wbn_bl1", "wbn_bl2", "wbn_bl3"],
      linkedNodes: { headline: "wbn_bl_eb", header: "wbn_bl_hd", text: "wbn_bl_tx" },
    },
    wbn_bl_eb: tc(BLOG, { isVisible: false, text: td("wbnBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_bl_hd: tc(BLOG, { isVisible: true, text: td("wbnBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_bl_tx: tc(BLOG, { isVisible: true, text: td("wbnBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_bl1: blogChild(BLOG, 0, td),
    wbn_bl2: blogChild(BLOG, 1, td),
    wbn_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("wbnFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["wbn_fq1", "wbn_fq2", "wbn_fq3", "wbn_fq4", "wbn_fq5", "wbn_fq6"],
      linkedNodes: { headline: "wbn_fq_eb", header: "wbn_fq_hd", text: "wbn_fq_tx" },
    },
    wbn_fq_eb: tc(FAQ, { isVisible: false, text: td("wbnFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wbn_fq_hd: tc(FAQ, { isVisible: true, text: td("wbnFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wbn_fq_tx: tc(FAQ, { isVisible: true, text: td("wbnFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wbn_fq1: faqChild(FAQ, 0, td),
    wbn_fq2: faqChild(FAQ, 1, td),
    wbn_fq3: faqChild(FAQ, 2, td),
    wbn_fq4: faqChild(FAQ, 3, td),
    wbn_fq5: faqChild(FAQ, 4, td),
    wbn_fq6: faqChild(FAQ, 5, td),

    // ── Map (online stream, hosted Berlin) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("wbnMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wbnMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wbnMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wbnMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-none",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("wbnMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wbn_mp_eb", header: "wbn_mp_hd", text: "wbn_mp_tx", button: "wbn_mp_btn" },
    },
    wbn_mp_eb: tc(MAP, { isVisible: false, text: td("wbnMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    wbn_mp_hd: tc(MAP, { isVisible: true, text: td("wbnMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wbn_mp_tx: tc(MAP, { isVisible: true, text: td("wbnMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wbn_mp_btn: btn(MAP, { text: td("wbnMapButton"), displayName: "button" }),

    // ── DataInput (multi-step registration) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("wbnDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wbnDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("wbnDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("wbnStepStatus"), td("wbnStepProfile"), td("wbnStepContext"), td("wbnStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "wbn_di_hl", header: "wbn_di_hd", text: "wbn_di_tx",
        fieldsGroup0: "wbn_di_f0", fieldsGroup1: "wbn_di_f1", fieldsGroup2: "wbn_di_f2",
        fieldsGroup3: "wbn_di_f3", fieldsGroup4: "wbn_di_f4",
        companionsGroup: "wbn_di_comp", overviewGroup: "wbn_di_ov", checkboxes: "wbn_di_cb",
        button: "wbn_di_btn", backButton: "wbn_di_back", nextButton: "wbn_di_next",
      },
    },
    wbn_di_hl: tc(DI, { isVisible: true, text: td("wbnDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    wbn_di_hd: tc(DI, { isVisible: true, text: td("wbnDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    wbn_di_tx: tc(DI, { isVisible: true, text: td("wbnDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    wbn_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wbn_di_acceptance", "wbn_di_first", "wbn_di_last", "wbn_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    wbn_di_acceptance: acceptanceFi("wbn_di_f0", td("acceptanceState")),
    wbn_di_first: fi("wbn_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wbn_di_last: fi("wbn_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wbn_di_email: fi("wbn_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wbn_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wbn_di_company", "wbn_di_role"], linkedNodes: {},
    },
    wbn_di_company: fi("wbn_di_f1", { label: td("wbnFieldCompany"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", changeable: true }),
    wbn_di_role: fi("wbn_di_f1", { label: td("wbnFieldRole"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "role", changeable: true }),
    wbn_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wbn_di_questions"], linkedNodes: {},
    },
    wbn_di_questions: fi("wbn_di_f2", { label: td("wbnFieldQuestions"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "questions", multiline: true, gridArea: "col-span-12", changeable: true }),
    wbn_di_f3: emptyFieldsGroup(DI, 3),
    wbn_di_f4: emptyFieldsGroup(DI, 4),
    wbn_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wbn_di_comp_first"], linkedNodes: {},
    },
    wbn_di_comp_first: fi("wbn_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    wbn_di_ov_t: tc("wbn_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    wbn_di_ov_x: tc("wbn_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    wbn_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["wbn_di_ov_t", "wbn_di_ov_x"], linkedNodes: {},
    },
    wbn_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    wbn_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("wbnDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    wbn_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    wbn_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("wbnContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wbnContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wbnContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "twitter", url: "https://twitter.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("wbnContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "wbn_ct_eb", header: "wbn_ct_hd", text: "wbn_ct_tx",
        firstName: "wbn_ct_first", lastName: "wbn_ct_last", eMail: "wbn_ct_email",
        phoneNumber: "wbn_ct_phone", Message: "wbn_ct_msg", button: "wbn_ct_btn",
      },
    },
    wbn_ct_eb: tc(CONTACT, { isVisible: true, text: td("wbnContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wbn_ct_hd: tc(CONTACT, { isVisible: true, text: td("wbnContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wbn_ct_tx: tc(CONTACT, { isVisible: true, text: td("wbnContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wbn_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    wbn_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    wbn_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    wbn_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    wbn_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    wbn_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("wbnContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────
// Bespoke meta sections so links/colors match the design (the shared
// appbar()/footer() leave the footer empty when there are no content pages).

/** White bar, blue rules, logo + nav + "Jetzt anmelden" CTA. */
export const webinarAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      // WS4-B2: deliberately NOT transparent — the hero is a light paper sheet,
      // so a see-through bar would sit light-on-light with no readable edge.
      backgroundColor: "#ffffff",
      border: true, borderColor: "#e5e7eb", borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("wbnLinkAgenda"), url: "/#wbn_agenda", linkType: "internal" },
        { label: td("wbnLinkSpeakers"), url: "/#wbn_speaker", linkType: "internal" },
        { label: td("wbnLinkFaq"), url: "/#wbn_faq", linkType: "internal" },
        { label: td("wbnLinkContact"), url: "/#wbn_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("wbnHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: "#ffffff", logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark footer, blue top rule, three link groups + social + copyright. */
export const webinarFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      // border-4 + borderSides:["top"] → getBorderClasses emits "border-t-4"
      // (passing "border-t-4" as the width would mis-emit "border-t-t-4").
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("wbnFooterProgram"), links: [
          { label: td("wbnLinkAgenda"), url: "/#wbn_agenda", linkType: "internal" },
          { label: td("wbnLinkSpeakers"), url: "/#wbn_speaker", linkType: "internal" },
          { label: td("wbnLinkReplay"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in a
        // new tab). URLs are placeholders the organiser edits per event.
        { title: td("wbnFooterEvent"), links: [
          { label: td("wbnLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Berlin", linkType: "external" },
          { label: td("wbnLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Executive+Briefing+Vol.+III", linkType: "external" },
          { label: td("wbnLinkTickets"), url: "/register", linkType: "internal" },
        ] },
        { title: td("wbnFooterSupport"), links: [
          { label: td("wbnLinkContact"), url: "/#wbn_contact", linkType: "internal" },
          { label: td("wbnLinkFaq"), url: "/#wbn_faq", linkType: "internal" },
          { label: td("wbnLinkPress"), url: "/#wbn_contact", linkType: "internal" },
        ] },
        { title: td("wbnFooterLegal"), links: [
          { label: td("wbnLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("wbnLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("wbnLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#94a3b8", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "twitter", platform: "twitter", url: "https://twitter.com" },
      ],
      socialColor: "#94a3b8", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#64748b", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B2) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero keeps its light split layout (photo right, node props untouched);
// the css layers the executive-briefing paper treatment underneath: a warm
// paper wash settling into the cream surface, a thin corporate rule at the
// top of the sheet, an ink hairline under the fold, uppercase letterspaced
// kickers, and the self-hosted faces so the preset's Georgia/Inter stacks
// resolve identically everywhere (Georgia = gelasio, the PDF-parity face).
//
// The preset itself lives in themes.ts (WEBINAR_PRESET) — it imports this.
export const webinarCustomCss = `/* theme:webinar */
${fontFaces([
  { family: "Georgia", file: "gelasio-regular", weightRange: "400" },
  { family: "Georgia", file: "gelasio-bold", weightRange: "700" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Briefing hero — warm paper sheet: white settles into the cream surface */
#wbn_hero {
${heroShell}
  background-image: linear-gradient(180deg, #ffffff 0%, ${CREAM} 100%);
}

/* Thin corporate rule across the top of the sheet (static) */
#wbn_hero::before {
${decorLayer}
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${ACCENT};
}

/* Ink hairline under the fold (draws itself on entrance) */
#wbn_hero::after {
${decorLayer}
  left: 6%;
  right: 6%;
  bottom: 8%;
  height: 1px;
  background: ${INK};
  opacity: 0.22;
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#wbn_hero .text-xs, #wbn_numbers .text-xs, #wbn_agenda .text-xs, #wbn_speaker .text-xs,
#wbn_text .text-xs, #wbn_image .text-xs, #wbn_countdown .text-xs, #wbn_gallery .text-xs,
#wbn_video .text-xs, #wbn_testimonials .text-xs, #wbn_blog .text-xs, #wbn_faq .text-xs,
#wbn_map .text-xs, #wbn_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("wbn", 16)}
  @keyframes wbnRuleGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  /* Quiet business entrance: the sheet settles, the hairline draws itself */
  #wbn_hero { animation: wbnFadeUp 0.7s ${EASE} both; }
  #wbn_hero::after {
    transform-origin: left center;
    animation: wbnRuleGrow 1.3s ${EASE} 0.4s both;
  }

${revealBlock("wbn", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;
